import { format } from 'date-fns';
import { ZODIAC_SIGNS, PLANETS, ELEMENTS, QUALITIES, RULING_PLANETS } from './constants';

// Compatibility scores (0-100)
export const COMPATIBILITY_SCORES = {
  Aries: {
    Aries: 70,
    Taurus: 60,
    Gemini: 80,
    Cancer: 50,
    Leo: 90,
    Virgo: 60,
    Libra: 80,
    Scorpio: 50,
    Sagittarius: 90,
    Capricorn: 50,
    Aquarius: 80,
    Pisces: 50
  },
  Taurus: {
    Aries: 60,
    Taurus: 80,
    Gemini: 60,
    Cancer: 80,
    Leo: 70,
    Virgo: 90,
    Libra: 70,
    Scorpio: 90,
    Sagittarius: 60,
    Capricorn: 90,
    Aquarius: 60,
    Pisces: 80
  },
  // Add other signs...
};

export class AstrologyService {
  private static OPENAI_API_KEY = process.env.EXPO_PUBLIC_OPENAI_API_KEY;
  private static OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

  static async getAstrologyInsight(query: string, userProfile: any, contextMessages?: { role: 'user' | 'assistant', content: string }[]): Promise<string> {
    try {
      
      if (!this.OPENAI_API_KEY) {
        throw new Error('OpenAI API key is not configured. Please check your .env file.');
      }

      const requestBody = {
        model: 'gpt-4.1-mini',
        messages: contextMessages
          ? [
              {
                role: 'system',
                content: `You are an expert astrologer with deep knowledge of Vedic astrology. You provide insightful, 
                personalized astrological guidance based on birth charts and current planetary positions.
                You are working with a Gen Z user so you need to explain it in a way that is easy to understand.
                Use language that is Gen Z friendly and use emojis to make the response more engaging.
                The user does not understand Vedic astrology, so you need to explain it in a way that is easy to understand.
                Avoid too much technical jargon. Make the response concise and to the point.
                Do not entertain any questions that are not related to astrology. 
                Do not entertain any questions that are not related to the user's birth chart.

                User Profile:
                Name: ${userProfile.name}
                Birth Date: ${userProfile.birthDate}
                Birth Time: ${userProfile.birthTime}
                Birth Place: ${userProfile.birthPlace}
                
                Please provide astrological insights that are:
                1. Personalized to the user's birth chart
                2. Clear and easy to understand
                3. Both practical and mystical
                4. Respectful of the user's free will
                5. Focused on growth and self-awareness`
              },
              ...contextMessages
            ]
          : [
              {
                role: 'system',
                content: `You are an expert astrologer with deep knowledge of Vedic astrology. You provide insightful, 
                personalized astrological guidance based on birth charts and current planetary positions. The user does not 
                understand Vedic astrology, so you need to explain it in a way that is easy to understand.
                You are working with a Gen Z user so you need to explain it in a way that is easy to understand.
                Use language that is Gen Z friendly and use emojis to make the response more engaging.
                Avoid too much technical jargon. Make the response concise and to the point.
                Do not entertain any questions that are not related to astrology. 
                Do not entertain any questions that are not related to the user's birth chart.
                
                User Profile:
                Name: ${userProfile.name}
                Birth Date: ${userProfile.birthDate}
                Birth Time: ${userProfile.birthTime}
                Birth Place: ${userProfile.birthPlace}
                
                Please provide astrological insights that are:
                1. Personalized to the user's birth chart
                2. Clear and easy to understand
                3. Both practical and mystical
                4. Respectful of the user's free will
                5. Focused on growth and self-awareness`
              },
              {
                role: 'user',
                content: query
              }
            ],
      };
      if (process.env.NODE_ENV === 'development') {
        console.log('OpenAI API Request', requestBody);
      }
      const response = await fetch(this.OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.OPENAI_API_KEY}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (process.env.NODE_ENV === 'development') {
        console.log('OpenAI API response:', data);
      }
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error(data.error?.message || 'Unexpected API response');
      }
      return data.choices[0].message.content;
    } catch (error: unknown) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting astrology insight:', error);
      }
      throw new Error('Failed to get astrology insight');
    }
  }

  // Get zodiac sign based on birth date
  static getZodiacSign(birthDate: string): string {
    const date = new Date(birthDate);
    const month = date.getMonth() + 1; // JavaScript months are 0-indexed
    const day = date.getDate();
    
    if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) {
      return 'Aries';
    } else if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) {
      return 'Taurus';
    } else if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) {
      return 'Gemini';
    } else if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) {
      return 'Cancer';
    } else if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) {
      return 'Leo';
    } else if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) {
      return 'Virgo';
    } else if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) {
      return 'Libra';
    } else if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) {
      return 'Scorpio';
    } else if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) {
      return 'Sagittarius';
    } else if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) {
      return 'Capricorn';
    } else if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) {
      return 'Aquarius';
    } else {
      return 'Pisces';
    }
  }

  // Get birth chart data (simplified)
  static getBirthChart(birthDate: string, birthTime: string, birthPlace: string) {
    // In a real app, this would use astronomical calculations
    // For now, we'll return mock data
    
    const zodiacSign = this.getZodiacSign(birthDate);
    
    return {
      sunSign: zodiacSign,
      moonSign: this.getRandomSign(zodiacSign),
      ascendant: this.getRandomSign(zodiacSign),
      planets: PLANETS.map(planet => ({
        name: planet,
        sign: this.getRandomSign(zodiacSign),
        degree: Math.floor(Math.random() * 30),
        house: Math.floor(Math.random() * 12) + 1
      })),
      houses: Array.from({ length: 12 }, (_, i) => ({
        number: i + 1,
        sign: this.getRandomSign(zodiacSign),
        degree: Math.floor(Math.random() * 30)
      }))
    };
  }

  // Get daily horoscope (simplified)
  static async getDailyHoroscope(userProfile: any): Promise<string> {
    try {
      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });

      const requestBody = {
        model: 'gpt-4.1-mini',
        messages: [
          {
            role: 'system',
            content: `You are an expert astrologer with deep knowledge of Vedic astrology. 
            You are working with a Gen Z user so you need to explain it in a way that is easy to understand.
            Use language that is Gen Z friendly and use emojis to make the response more engaging.
            Create a personalized daily horoscope that is:
            1. Based on the user's birth chart and current planetary positions
            2. Focused on practical guidance and opportunities
            3. Both mystical and practical
            4. Respectful of free will
            5. Focused on growth and self-awareness
            
            User Profile:
            Name: ${userProfile.name}
            Birth Date: ${userProfile.birthDate}
            Birth Time: ${userProfile.birthTime}
            Birth Place: ${userProfile.birthPlace}
            
            Current Date: ${formattedDate}
            
            Format the response as a clear, well-structured markdown message with sections for:
            - Overall Energy
            - Opportunities
            - Challenges
            - Advice
            - Lucky Numbers & Colors
            
            Keep each section concise but meaningful.`
          }
        ]
      };

      const response = await fetch(this.OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.OPENAI_API_KEY}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error(data.error?.message || 'Unexpected API response');
      }
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error getting daily horoscope:', error);
      throw new Error('Failed to get daily horoscope');
    }
  }

  // Get compatibility between two signs
  static getCompatibility(sign1: string, sign2: string, type: 'romantic' | 'friendship' | 'professional' = 'romantic') {
    // In a real app, this would use more complex calculations
    // For now, we'll return mock data based on the signs
    
    const baseScore = COMPATIBILITY_SCORES[sign1 as keyof typeof COMPATIBILITY_SCORES]?.[sign2 as keyof typeof COMPATIBILITY_SCORES] || 70;
    
    // Adjust score based on compatibility type
    let adjustedScore = baseScore;
    if (type === 'friendship') {
      adjustedScore = Math.min(100, baseScore + 10);
    } else if (type === 'professional') {
      adjustedScore = Math.max(0, baseScore - 10);
    }
    
    // Generate compatibility aspects
    const aspects = [
      {
        name: 'Communication',
        score: Math.min(100, adjustedScore + (Math.random() > 0.5 ? 5 : -5)),
        description: this.getAspectDescription('Communication', sign1, sign2)
      },
      {
        name: 'Emotional',
        score: Math.min(100, adjustedScore + (Math.random() > 0.5 ? 5 : -5)),
        description: this.getAspectDescription('Emotional', sign1, sign2)
      },
      {
        name: 'Values',
        score: Math.min(100, adjustedScore + (Math.random() > 0.5 ? 5 : -5)),
        description: this.getAspectDescription('Values', sign1, sign2)
      }
    ];
    
    return {
      overallScore: adjustedScore,
      aspects,
      description: this.getCompatibilityDescription(sign1, sign2, adjustedScore)
    };
  }

  // Helper method to get a random sign (excluding the given sign)
  private static getRandomSign(excludeSign: string): string {
    const availableSigns = ZODIAC_SIGNS.filter(sign => sign !== excludeSign);
    return availableSigns[Math.floor(Math.random() * availableSigns.length)];
  }

  // Helper method to get compatibility description
  private static getCompatibilityDescription(sign1: string, sign2: string, score: number): string {
    if (score >= 90) {
      return `${sign1} and ${sign2} have an exceptional connection. Your elements complement each other perfectly, creating a harmonious and fulfilling relationship.`;
    } else if (score >= 80) {
      return `${sign1} and ${sign2} have a strong connection. Your elements work well together, creating a balanced and supportive relationship.`;
    } else if (score >= 70) {
      return `${sign1} and ${sign2} have a good connection. While there may be some challenges, your elements can work together with understanding and compromise.`;
    } else if (score >= 60) {
      return `${sign1} and ${sign2} have a moderate connection. Your elements may clash at times, but with effort, you can find common ground and build a stable relationship.`;
    } else {
      return `${sign1} and ${sign2} have a challenging connection. Your elements may conflict, creating tension and misunderstandings. This relationship will require significant effort and patience.`;
    }
  }

  // Helper method to get aspect description
  private static getAspectDescription(aspect: string, sign1: string, sign2: string): string {
    const element1 = ELEMENTS[sign1 as keyof typeof ELEMENTS];
    const element2 = ELEMENTS[sign2 as keyof typeof ELEMENTS];
    
    if (element1 === element2) {
      return `${sign1} and ${sign2} share the same ${element1} element, creating a natural understanding in this area.`;
    } else if (
      (element1 === 'Fire' && element2 === 'Air') ||
      (element1 === 'Air' && element2 === 'Fire') ||
      (element1 === 'Earth' && element2 === 'Water') ||
      (element1 === 'Water' && element2 === 'Earth')
    ) {
      return `${sign1}'s ${element1} element complements ${sign2}'s ${element2} element, creating a harmonious balance in this area.`;
    } else {
      return `${sign1}'s ${element1} element and ${sign2}'s ${element2} element may clash in this area, requiring understanding and compromise.`;
    }
  }

  static async getBirthChartSummary(userProfile: any): Promise<string> {
    try {
      const birthChart = this.getBirthChart(userProfile.birthDate, userProfile.birthTime, userProfile.birthPlace);
      const zodiacSign = this.getZodiacSign(userProfile.birthDate);
      
      const requestBody = {
        model: 'gpt-4.1-mini',
        messages: [
          {
            role: 'system',
            content: `You are an expert astrologer with deep knowledge of Vedic astrology. 
            You are working with a Gen Z user so you need to explain it in a way that is easy to understand.
            Use language that is Gen Z friendly and use emojis to make the response more engaging.
            Create a personalized birth chart summary that is:
            1. Easy to understand for someone new to astrology
            2. Focused on the key aspects of their chart
            3. Both practical and mystical
            4. Respectful of their free will
            5. Focused on growth and self-awareness
            
            User Profile:
            Name: ${userProfile.name}
            Birth Date: ${userProfile.birthDate}
            Birth Time: ${userProfile.birthTime}
            Birth Place: ${userProfile.birthPlace}
            
            Birth Chart Data:
            Sun Sign: ${birthChart.sunSign}
            Moon Sign: ${birthChart.moonSign}
            Ascendant: ${birthChart.ascendant}
            
            Format the response as a clear, well-structured markdown message with sections for:
            - Key Personality Traits
            - Life Path & Purpose
            - Strengths & Challenges
            - Relationship Style
            - Career & Life Direction
            
            Keep each section concise but meaningful.`
          }
        ]
      };

      const response = await fetch(this.OPENAI_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.OPENAI_API_KEY}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error(data.error?.message || 'Unexpected API response');
      }
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error getting birth chart summary:', error);
      throw new Error('Failed to get birth chart summary');
    }
  }
} 
